import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from "firebase/storage";
import { storage } from "@/firebase";

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export interface FileMetadata {
  name: string;
  size: number;
  type: string;
  url: string;
  path: string;
  createdAt: Date;
}

/**
 * Upload a file to Firebase Storage
 * @param file - The file to upload
 * @param path - The storage path (e.g., 'users/userId/profile-pictures/')
 * @param fileName - Optional custom filename
 * @returns Promise<UploadResult>
 */
export async function uploadFile(
  file: File,
  path: string,
  fileName?: string
): Promise<UploadResult> {
  try {
    const finalFileName = fileName || file.name;
    const storageRef = ref(storage, `${path}${finalFileName}`);

    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      success: true,
      url: downloadURL,
    };
  } catch (error) {
    console.error("Error uploading file:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Get download URL for a file
 * @param path - The storage path to the file
 * @returns Promise<string | null>
 */
export async function getFileURL(path: string): Promise<string | null> {
  try {
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    console.error("Error getting file URL:", error);
    return null;
  }
}

/**
 * Delete a file from Firebase Storage
 * @param path - The storage path to the file
 * @returns Promise<boolean>
 */
export async function deleteFile(path: string): Promise<boolean> {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    console.error("Error deleting file:", error);
    return false;
  }
}

/**
 * List all files in a directory
 * @param path - The storage directory path
 * @returns Promise<FileMetadata[]>
 */
export async function listFiles(path: string): Promise<FileMetadata[]> {
  try {
    const storageRef = ref(storage, path);
    const result = await listAll(storageRef);

    const files: FileMetadata[] = [];

    for (const item of result.items) {
      try {
        const url = await getDownloadURL(item);
        files.push({
          name: item.name,
          size: 0, // Size not available without metadata
          type: "", // Type not available without metadata
          url,
          path: item.fullPath,
          createdAt: new Date(),
        });
      } catch (error) {
        console.error(`Error getting URL for ${item.name}:`, error);
      }
    }

    return files;
  } catch (error) {
    console.error("Error listing files:", error);
    return [];
  }
}

/**
 * Upload multiple files
 * @param files - Array of files to upload
 * @param path - The storage path
 * @returns Promise<UploadResult[]>
 */
export async function uploadMultipleFiles(files: File[], path: string): Promise<UploadResult[]> {
  const results: UploadResult[] = [];

  for (const file of files) {
    const result = await uploadFile(file, path);
    results.push(result);
  }

  return results;
}

/**
 * Generate a unique filename to avoid conflicts
 * @param originalName - Original filename
 * @returns string
 */
export function generateUniqueFileName(originalName: string): string {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const extension = originalName.split(".").pop();
  const nameWithoutExtension = originalName.replace(`.${extension}`, "");

  return `${nameWithoutExtension}_${timestamp}_${randomString}.${extension}`;
}
